import { tx } from "../lib/i18n";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useMeta } from "../lib/useMeta";
import { useAuth } from "../context/AuthContext";
import { Input } from "./Auth";
import { PageHero } from "../components/site/Layout";

export const MultiPick = ({ label, options, value, onChange, testid }) => (
  <div data-testid={testid}>
    <span className="osk-label">{label}</span>
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const on = value.includes(o);
        return (
          <button type="button" key={o} onClick={() => onChange(on ? value.filter((x) => x !== o) : [...value, o])} data-testid={`${testid}-${o}`}
            className={`rounded-full border px-3 py-1.5 text-sm font-semibold transition-colors ${on ? "border-osk-blue bg-osk-blue text-white" : "border-slate-200 bg-white text-osk-navy hover:border-osk-blue"}`}>{o}</button>
        );
      })}
    </div>
  </div>
);

export const TeacherProfileFields = ({ form, setForm, meta }) => {
  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });
  return (
    <>
      <Input label={tx("Weergavenaam (bijv. Mw. S. de Palm)")} required value={form.display_name} onChange={set("display_name")} testid="teacher-display-name-input" />
      <label className="block"><span className="osk-label">{tx("Over jou (bio)")}</span>
        <textarea className="osk-input min-h-[120px]" required value={form.bio} onChange={set("bio")} data-testid="teacher-bio-input"
          placeholder={tx("Vertel over je ervaring en manier van lesgeven. Contactgegevens zijn niet toegestaan.")} />
      </label>
      <MultiPick label={tx("Vakken")} options={meta?.subjects || []} value={form.subjects} onChange={set("subjects")} testid="teacher-subjects" />
      <MultiPick label={tx("Schooltypes")} options={meta?.school_types || []} value={form.school_types} onChange={set("school_types")} testid="teacher-school-types" />
      <MultiPick label={tx("Talen")} options={meta?.languages || []} value={form.languages} onChange={set("languages")} testid="teacher-languages" />
      <label className="block"><span className="osk-label">{tx("Eiland")}</span>
        <select className="osk-input" value={form.island} onChange={set("island")} data-testid="teacher-island-select">
          {(meta?.islands || []).map((i) => <option key={i.code} value={i.code} disabled={!i.enabled}>{i.name}{i.enabled ? "" : ` ${tx("(binnenkort)")}`}</option>)}
        </select>
      </label>
      <div className="grid gap-4 sm:grid-cols-3">
        <Input label={tx("Bank")} required value={form.bank_name} onChange={set("bank_name")} testid="teacher-bank-name-input" />
        <Input label={tx("Rekeningnummer")} required value={form.bank_account} onChange={set("bank_account")} testid="teacher-bank-account-input" />
        <Input label={tx("Tenaamstelling")} required value={form.bank_holder} onChange={set("bank_holder")} testid="teacher-bank-holder-input" />
      </div>
    </>
  );
};

export default function TeacherApply() {
  const { lang } = useI18n();
  const meta = useMeta();
  const { signIn } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", display_name: "", bio: "", subjects: [], school_types: [], languages: [],
    island: "aruba", bank_name: "", bank_account: "", bank_holder: "", accept_terms: false });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.accept_terms) return toast.error(tx("Accepteer de Algemene Voorwaarden voor Docenten"));
    if (!form.subjects.length || !form.school_types.length || !form.languages.length) return toast.error(tx("Kies minimaal één vak, schooltype en taal"));
    setBusy(true);
    try {
      const { data } = await api.post("/auth/apply-teacher", { ...form, language: lang });
      await signIn(data);
      toast.success(tx("Aanmelding ontvangen! Upload nu je documenten."));
      nav("/docent?tab=profile");
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <div data-testid="teacher-apply-page">
      <PageHero title={tx("Word docent bij Onskola")} sub={tx("Meld je aan, upload daarna je ID, diploma's en verklaring omtrent gedrag. Na goedkeuring kun je lessen publiceren.")} />
      <div className="osk-container max-w-3xl pb-10">
        <form onSubmit={submit} className="osk-card space-y-5 p-6 md:p-8" data-testid="teacher-apply-form">
          <h3 className="text-lg">{tx("Account")}</h3>
          <div className="grid gap-4 sm:grid-cols-3">
            <Input label={tx("Volledige naam")} required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} testid="apply-name-input" />
            <Input label={tx("E-mailadres")} type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} testid="apply-email-input" />
            <Input label={tx("Wachtwoord")} type="password" minLength={8} required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} testid="apply-password-input" />
          </div>
          <h3 className="pt-2 text-lg">{tx("Profiel & uitbetaling")}</h3>
          <TeacherProfileFields form={form} setForm={setForm} meta={meta} />
          <label className="flex items-start gap-3 text-sm text-osk-text">
            <input type="checkbox" className="mt-1 h-4 w-4 accent-[#1565D8]" checked={form.accept_terms} onChange={(e) => setForm({ ...form, accept_terms: e.target.checked })} data-testid="apply-terms-checkbox" />
            <span>{tx("Ik ga akkoord met de")} <Link to="/voorwaarden/docenten" target="_blank" className="font-bold text-osk-blue">{tx("Algemene Voorwaarden voor Docenten")}</Link> {tx("en het")} <Link to="/privacy" target="_blank" className="font-bold text-osk-blue">{tx("Privacybeleid")}</Link>.</span>
          </label>
          <button className="osk-btn-primary w-full sm:w-auto" disabled={busy} data-testid="apply-submit-btn">{tx("Aanmelding versturen")}</button>
        </form>
      </div>
    </div>
  );
}
